import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Drawer, Form } from 'antd';
import { ReactComponent as SaveIcon } from '@/assets/images/icons/save_icon.svg';
import { ProfileService } from '@/services';
import ImageUpload from '@/components/ImageUpload';
import InputComponent from '@/components/Input';
import SelectInput from '@/components/Select';
import ButtonComponent from '@/components/Button';
import TimezoneSelector from '@/components/TimezoneSelector';

import './index.scss';

function ModalAccountComponent({ open, onClose, profileData }) {
  const [url, setUrl] = useState();
  const [formInstance] = Form.useForm();
  const { t, i18n } = useTranslation();
  const [loadingSave, setLoadingSave] = useState(false);

  const handleUpdateAvatar = useCallback(async () => {
    if (url) {
      const { success } = await ProfileService.updateAvatar(url);
      if (success) {
        setUrl('');
      }
      return success;
    }

    return null;
  }, [url]);

  const handleUpdateProfile = async (values) => {
    const res = await ProfileService.updateProfile(values);

    if (res.success && formInstance.isFieldTouched('language')) {
      const userLanguage = values.language === 'en' ? 'en_US' : values.language;
      i18n.changeLanguage(userLanguage);
      window.location.reload(false);
    }

    return res.success;
  };

  const handleCloseForm = () => {
    onClose();
    formInstance.resetFields();
  };

  const handleSubmitForm = async (values) => {
    setLoadingSave(true);
    const payload = values;
    const { timezone } = values;

    if (typeof timezone === 'object') {
      payload.timezone = timezone.value;
    }

    Promise.all([handleUpdateProfile(payload), handleUpdateAvatar()]).then(
      (response) => {
        const updatedProfile = response[0];
        const updatedAvatar = response[1];
        const updatedSuccess =
          (updatedProfile && updatedAvatar) ||
          updatedProfile ||
          (updatedAvatar !== null && updatedAvatar);

        if (updatedSuccess) {
          onClose();
        }
        setLoadingSave(false);
      },
    );
  };

  return (
    <Drawer
      title={t('profile_page.drawer.account')}
      className="drawer"
      placement="right"
      onClose={handleCloseForm}
      open={open}
      width={573}
      destroyOnClose
      keyboard={false}
      maskClosable={false}
    >
      <ImageUpload
        account={profileData}
        url={profileData?.avatar}
        setUrl={setUrl}
      />
      <Form
        form={formInstance}
        className="account-form"
        name="account-form"
        onFinish={handleSubmitForm}
        style={{
          maxWidth: 600,
        }}
        initialValues={profileData}
      >
        <Form.Item label={<span>{t('profile_page.account.merchant_id')}</span>}>
          <InputComponent
            placeholder={t('profile_page.placehoder.merchant_id')}
            disabled
            value={profileData.id}
          />
        </Form.Item>
        <Form.Item label={t('login.form.fields.email')}>
          <InputComponent
            placeholder={t('login.form.placeholder.email')}
            disabled
            value={profileData.email}
          />
        </Form.Item>
        <Form.Item
          name="firstName"
          label={t('sign_up.form.fields.first_name')}
          rules={[
            {
              required: true,
              message: t('sign_up.form.errors.first_name.required'),
            },
          ]}
        >
          <InputComponent
            placeholder={t('sign_up.form.placeholder.first_name')}
          />
        </Form.Item>
        <Form.Item
          name="lastName"
          label={t('sign_up.form.fields.last_name')}
          rules={[
            {
              required: true,
              message: t('sign_up.form.errors.last_name.required'),
            },
          ]}
        >
          <InputComponent
            placeholder={t('sign_up.form.placeholder.last_name')}
          />
        </Form.Item>
        <Form.Item
          name="timezone"
          label={t('profile_page.account.timezone')}
          rules={[
            {
              required: true,
              message: t('profile_page.errols.timezone.require'),
            },
          ]}
        >
          <TimezoneSelector />
        </Form.Item>
        <Form.Item
          name="language"
          label={t('profile_page.account.language')}
          rules={[
            {
              required: true,
              message: t('profile_page.errols.language.require'),
            },
          ]}
        >
          <SelectInput
            placeholder={t('profile_page.placehoder.language')}
            options={[
              { value: 'en_US', label: 'English' },
              { value: 'th', label: 'Thailand' },
            ]}
          />
        </Form.Item>
        <Form.Item
          wrapperCol={{
            offset: 8,
          }}
          className="form-btn"
        >
          <ButtonComponent
            onClick={handleCloseForm}
            className="btn-exit"
            disabled={loadingSave}
          >
            {t('profile_page.btn_discard')}
          </ButtonComponent>
          <ButtonComponent
            type="primary"
            htmlType="submit"
            className="btn-saveChanges"
            icon={<SaveIcon />}
            disabled={loadingSave}
            loading={loadingSave}
          >
            {t('profile_page.btn_save')}
          </ButtonComponent>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

ModalAccountComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  profileData: PropTypes.object.isRequired,
};

export default ModalAccountComponent;
